import Alert, { AlertColor as Color } from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import React, { createContext, useContext, useState } from 'react'

interface SnackbarItem {
  key: number
  message: string
  severity: Color
  autoHideDuration: number | null
}

interface SnackbarContextType {
  openSnackbar: (
    key: number,
    message: string,
    severity?: Color,
    autoHideDuration?: number | null
  ) => void
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined)

interface SnackbarProviderProps {
  children: React.ReactNode
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
  const [snackbars, setSnackbars] = useState<SnackbarItem[]>([])

  const openSnackbar = (
    key: number,
    message: string,
    severity: Color = 'info',
    autoHideDuration: number | null = null
  ) => {
    setSnackbars([{ key, message, severity, autoHideDuration }])
  }

  const handleClose = (key: number) => {
    setSnackbars((prev) => prev.filter((snackbar) => snackbar.key !== key))
  }

  return (
    <SnackbarContext.Provider value={{ openSnackbar }}>
      {children}
      {snackbars.map((snackbar) => (
        <Snackbar
          key={snackbar.key}
          open
          autoHideDuration={snackbar.autoHideDuration}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          onClose={() => handleClose(snackbar.key)}
        >
          <Alert
            onClose={() => handleClose(snackbar.key)}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      ))}
    </SnackbarContext.Provider>
  )
}

export const useSnackbar = (): SnackbarContextType => {
  const context = useContext(SnackbarContext)
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider')
  }
  return context
}




